<template>
  <b-modal id="groupFacultiesModal" :title="group.name ? `Staff - ${group.name}` : 'Staff'" size="lg" no-close-on-backdrop scrollable>

    <div class="loading" v-if="fetchData">
      <load-data></load-data>
    </div>

    <div v-if="!fetchData">

      <!-- View mode: assigned faculties list -->
      <div v-if="!isAddingFaculty">
        <table class="table table-striped table-bordered">
          <thead class="thead-dark text-left">
            <th style="width:1%;white-space:nowrap">Sl No.</th>
            <th>Name</th>
            <th>Email</th>
            <th style="width:1%;white-space:nowrap">Action</th>
          </thead>
          <tbody>
            <tr v-if="faculties.length === 0">
              <td colspan="4" class="text-center">No staff found</td>
            </tr>
            <tr v-for="(faculty, index) in sortedFaculties" :key="faculty.id">
              <td>{{ index + 1 }}</td>
              <td>{{ faculty.institution_user.first_name }} {{ faculty.institution_user.last_name }}</td>
              <td>{{ faculty.institution_user.email}}</td>
              <td>
                <span
                  v-if="removingFacultyId !== faculty.id"
                  @click="removeFaculty(faculty)"
                  class="text-danger"
                  style="cursor:pointer;font-size:1.1rem;"
                  title="Remove"
                >
                  <i class="fas fa-trash-alt"></i>
                </span>
                <span v-else class="text-muted" style="font-size:0.8rem;">Removing...</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Add mode: checkbox table -->
      <div v-else>
        <table class="table table-striped table-bordered">
          <thead class="thead-dark text-left">
            <th style="width:40px">
              <input type="checkbox" :checked="allSelectableSelected" @change="toggleSelectAll" />
            </th>
            <th style="width:1%;white-space:nowrap">Sl No.</th>
            <th>Name</th>
            <th>Email</th>
          </thead>
          <tbody>
            <tr v-if="institutionFaculties.length === 0">
              <td colspan="3" class="text-center">No staff found</td>
            </tr>
            <tr v-for="(faculty, index) in institutionFaculties" :key="faculty.id">
              <td>
                <input type="checkbox" :value="faculty.id" v-model="selectedFacultyIds"
                  :disabled="otherGroupFacultyIds.includes(faculty.id) || originalFacultyIds.includes(faculty.id)" />
              </td>
              <td>{{ index + 1 }}</td>
              <td>{{ faculty.name }}</td>
              <td>{{ faculty.email }}</td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>

    <template #modal-footer>
      <template v-if="!isAddingFaculty">
        <button class="btn btn-primary" @click="openAddFaculty">Add Staff</button>
        <button class="btn btn-secondary" @click="$bvModal.hide('groupFacultiesModal')">Close</button>
      </template>
      <template v-else>
        <button class="btn btn-primary" @click="assignFaculty" :disabled="fetchData">Submit</button>
        <button class="btn btn-secondary" @click="isAddingFaculty = false">Cancel</button>
      </template>
    </template>

  </b-modal>
</template>

<script>
import axios from 'axios';
export default {
  props: {
    faculties: {
      type: Array,
      default: () => [],
    },
    group: {
      type: Object,
      default: () => ({}),
    },
    leaveGroups: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    // assigned faculties in alphabetical order of name
    sortedFaculties() {
      const fullName = (f) => `${f.institution_user.first_name} ${f.institution_user.last_name || ''}`.trim();
      return [...this.faculties].sort((a, b) => fullName(a).localeCompare(fullName(b), undefined, { sensitivity: 'base' }));
    },
    otherGroupFacultyIds() {
      return this.leaveGroups
        .filter((g) => g.id !== this.group.id)
        .flatMap((g) => (g.faculties || []).map((f) => f.institution_user.id));
    },
    selectableFacultyIds() {
      return this.institutionFaculties
        .filter((f) => !this.otherGroupFacultyIds.includes(f.id) && !this.originalFacultyIds.includes(f.id))
        .map((f) => f.id);
    },
    allSelectableSelected() {
      return this.selectableFacultyIds.length > 0 &&
        this.selectableFacultyIds.every((id) => this.selectedFacultyIds.includes(id));
    },
  },
  data() {
    return {
      fetchData: false,
      isAddingFaculty: false,
      institutionFaculties: [],
      selectedFacultyIds: [],
      originalFacultyIds: [],
      removingFacultyId: null,
    };
  },
  watch: {
    group: {
      immediate: true,
      handler(newGroup) {
        this.isAddingFaculty = false;
        if (newGroup && newGroup.id) {
          const existing = (newGroup.faculties || []).map((f) => f.institution_user.id);
          this.originalFacultyIds = [...existing];
          this.selectedFacultyIds = [...existing];
        }
      },
    },
  },
  methods: {
    async openAddFaculty() {
      if (!this.institutionFaculties.length) {
        await this.getFaculties();
      }
      this.isAddingFaculty = true;
    },

    toggleSelectAll(event) {
      if (event.target.checked) {
        const toAdd = this.selectableFacultyIds.filter((id) => !this.selectedFacultyIds.includes(id));
        this.selectedFacultyIds = [...this.selectedFacultyIds, ...toAdd];
      } else {
        this.selectedFacultyIds = this.selectedFacultyIds.filter((id) => !this.selectableFacultyIds.includes(id));
      }
    },

    async assignFaculty() {
      const newlyAdded = this.selectedFacultyIds.filter((id) => !this.originalFacultyIds.includes(id));
      if (!newlyAdded.length) {
        this.isAddingFaculty = false;
        return;
      }

      const url = this.$store.getters.getAPIKey.mainAPI + `/faculty-leave/group/assign`;

      await axios.post(url, {
        faculties: newlyAdded,
        leave_cycle_id: this.group.leave_cycle_id,
        leave_group_id: this.group.id,
      })
        .then(() => {
          this.$toast.success("Staff updated", {
            position: "top",
            duration: 3000,
          });
          this.isAddingFaculty = false;
          this.$emit("assigned");
          this.$bvModal.hide('groupFacultiesModal');
        })
        .catch(() => {});
    },

    async removeFaculty(faculty) {
      const name = `${faculty.institution_user.first_name} ${faculty.institution_user.last_name}`.trim();
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Remove ${name} from this group will cancel all pending leave requests?`,
        {
          title: 'Confirm Remove',
          okTitle: 'Remove',
          okVariant: 'danger',
          cancelTitle: 'Cancel',
          centered: true,
        }
      );
      if (!confirmed) return;

      this.removingFacultyId = faculty.id;
      const url = this.$store.getters.getAPIKey.mainAPI + `/faculty-leave/group/unassign`;
      await axios.post(url, {
        leave_cycle_id: this.group.leave_cycle_id,
        institution_user_id: faculty.institution_user.id,
      })
        .then(() => {
          this.$toast.success(`${name} removed from group`, { position: 'top', duration: 3000 });
          this.$emit('assigned');
        })
        .catch(() => {
          this.$toast.error('Failed to remove staff', { position: 'top', duration: 3000 });
        });
      this.removingFacultyId = null;
    },

    async getFaculties() {
      this.fetchData = true;

      const url = this.$store.getters.getAPIKey.mainAPI + `/institutionusers/faculties`;

      await axios.get(url)
        .then((response) => {
          this.institutionFaculties = response.data.map((f) => ({
            id: f.id,
            name: [f.first_name, f.last_name].filter(Boolean).join(' '),
            email: f.email
          })).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        })
        .catch(() => {
          this.$toast.error("Failed to load staff", {
            position: "top",
            duration: 3000,
          });
        });

      this.fetchData = false;
    },
  },
};
</script>
